import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MUIButton from '@mui/material/Button';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import CenterFlexBox from 'components/CenterFlexBox';

const useStyles = makeStyles(theme => ({
    root: {
        width: '100%',
        position: 'relative',
        padding: '96px 32px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        overflow: 'hidden',
        [theme.breakpoints.down('md')]: {
            padding: '64px 16px',
        },
    },

    // ── Grid background ─────────────────────────────────
    gridBackground: {
        position: 'absolute',
        inset: 0,
        backgroundImage:
            'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),' +
            'linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
        backgroundSize: '40px 40px',
        pointerEvents: 'none',
    },

    // ── Heading ─────────────────────────────────────────
    headingWrapper: {
        position: 'relative',
        zIndex: 1,
        textAlign: 'center',
        maxWidth: '900px',
        marginBottom: '40px',
    },
    headingText: {
        color: '#FFFFFF',
        fontFamily: 'Mulish, sans-serif',
        fontWeight: 800,
        fontSize: '3rem',
        lineHeight: 1.4,
        [theme.breakpoints.down('md')]: {
            fontSize: '1.8rem',
        },
        [theme.breakpoints.down('sm')]: {
            fontSize: '1.4rem',
        },
    },

    // ── Inline image placeholders ───────────────────────
    inlineImage: {
        display: 'inline-block',
        width: '64px',
        height: '40px',
        borderRadius: '10px',
        backgroundColor: '#333651',
        verticalAlign: 'middle',
        margin: '0 6px',
        objectFit: 'cover',
        [theme.breakpoints.down('md')]: {
            width: '48px',
            height: '30px',
            borderRadius: '8px',
        },
    },

    // ── CTA ─────────────────────────────────────────────
    ctaWrapper: {
        position: 'relative',
        zIndex: 1,
    },
    ctaButton: {
        backgroundColor: '#EE634E',
        color: '#FFFFFF',
        borderRadius: '50px',
        padding: '12px 28px',
        textTransform: 'none',
        fontWeight: 700,
        fontSize: '0.95rem',
        fontFamily: 'Mulish, sans-serif',
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: '#d9563f',
            boxShadow: 'none',
        },
    },
    ctaArrow: {
        backgroundColor: '#EE634E',
        color: '#FFFFFF',
        borderRadius: '50%',
        minWidth: '44px',
        width: '44px',
        height: '44px',
        padding: 0,
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: '#d9563f',
            boxShadow: 'none',
        },
    },
}));

function CTABanner({
    heading,
    subtitle: subtitleText,
    ctaText = 'Check Your IELTS Band for Free',
    ctaHref = '/practice#practice-by-tests',
    hidePrimaryCta = false,
    secondaryCtaText,
    secondaryCtaHref,
}) {
    const classes = useStyles();

    return (
        <Box className={classes.root}>

            {/* Grid pattern background */}
            <Box className={classes.gridBackground} />

            {/* Heading */}
            <Box className={classes.headingWrapper}>
                {heading ? (
                    <Typography component="h2" className={classes.headingText}>
                        {heading}
                    </Typography>
                ) : (
                    <Typography component="h2" className={classes.headingText}>
                        Give your{' '}
                        <Box component="img" src="https://assets.languify.in/images/ielts_cta1.jpg" alt="" className={classes.inlineImage} />
                        {' '}First IELTS mock test and Discover How Personalized{' '}
                        <Box component="img" src="https://assets.languify.in/images/ielts_cta2.jpg" alt="" className={classes.inlineImage} />
                        {' '}Feedback Can{' '}
                        <Box component="img" src="https://assets.languify.in/images/ielts_cta3.jpg" alt="" className={classes.inlineImage} />
                        {' '}Transform Your Confidence.
                    </Typography>
                )}
            </Box>

            {/* Subtitle */}
            {subtitleText && (
                <Typography
                    component="p"
                    sx={{
                        color: '#E6E6E6',
                        fontFamily: 'Mulish, sans-serif',
                        fontSize: '1rem',
                        textAlign: 'center',
                        mb: '32px',
                        position: 'relative',
                        zIndex: 1,
                    }}
                >
                    {subtitleText}
                </Typography>
            )}

            {/* CTA buttons */}
            <CenterFlexBox gap="12px" className={classes.ctaWrapper} flexWrap="wrap">
                {!hidePrimaryCta && (
                    <MUIButton
                        variant="contained"
                        disableElevation
                        component="a"
                        href={ctaHref}
                        className={classes.ctaButton}
                    >
                        {ctaText}
                    </MUIButton>
                )}
                {secondaryCtaText ? (
                    <MUIButton
                        variant="outlined"
                        disableElevation
                        component="a"
                        href={secondaryCtaHref || ctaHref}
                        sx={{
                            color: '#EE634E',
                            border: '1.5px solid #EE634E',
                            borderRadius: '50px',
                            padding: '12px 28px',
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.95rem',
                            fontFamily: 'Mulish, sans-serif',
                            '&:hover': {
                                backgroundColor: 'rgba(238,99,78,0.1)',
                                border: '1.5px solid #EE634E',
                            },
                        }}
                    >
                        {secondaryCtaText}
                    </MUIButton>
                ) : (
                    <MUIButton
                        variant="contained"
                        disableElevation
                        component="a"
                        href={ctaHref}
                        className={classes.ctaArrow}
                    >
                        <NorthEastIcon fontSize="small" />
                    </MUIButton>
                )}
            </CenterFlexBox>

        </Box>
    );
}

export default CTABanner;
